import React, { forwardRef, useCallback, useEffect, useMemo } from 'react';
import { styled } from '@compiled/react';
import isNumber from 'lodash/isNumber';
import toNumber from 'lodash/toNumber';
import BadgeDI from '@atlaskit/badge';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { MenuItem as MenuItemDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import type { MatcherLocation } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/types.tsx';
import { formatDistanceStrictWithLocale } from '@atlassian/jira-platform-utils-date-fns/src/main.tsx';
import { useQueueCustomRouterContext } from '@atlassian/jira-servicedesk-common/src/navigation/queues/use-queue-custom-router-context/index.tsx';
import {
	QueueListSection,
	useSelectedGroupIdByCategory,
	useSelectedQueueSectionByCategory,
} from '@atlassian/jira-servicedesk-queues-categorized-store/src/controllers/navigation-category/index.tsx';
import { usePriorityGroups } from '@atlassian/jira-servicedesk-queues-categorized-store/src/controllers/priority-group/index.tsx';
import { NO_CATEGORY } from '@atlassian/jira-servicedesk-work-category/src/common/constants.tsx';
import { toItsmPractice } from '@atlassian/jira-servicedesk-work-category/src/main.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { usePathParam, useRouter } from '@atlassian/react-resource-router';
import IssueCountErrorMessage from './issue-count-error-message/main.tsx';
import { tooltipMessages } from './messages.tsx';
import { StarButton } from './star-button/index.tsx';
import type { QueueItemProps } from './types.tsx';
import { calculateSelectedSection } from './utils.tsx';

const MAX_BADGE_COUNT = 999;

export const matchUrlForSelectedQueue =
	(
		pathCategorySelectedSection: QueueListSection | undefined,
		currentQueueInSection: QueueListSection,
	) =>
	(currentLocation: MatcherLocation, itemLocation: MatcherLocation) => {
		const pathNameMatches =
			currentLocation.pathname === itemLocation.pathname ||
			currentLocation.pathname.substring(0, currentLocation.pathname.lastIndexOf('/')) ===
				itemLocation.pathname;

		const isSectionMatched = pathCategorySelectedSection === currentQueueInSection;

		if (!fg('jsm_views_inside_queues_-_main_flag')) {
			return pathNameMatches && isSectionMatched;
		}

		const pathNameMatchesNew =
			currentLocation.pathname === itemLocation.pathname ||
			currentLocation.pathname.substring(0, currentLocation.pathname.lastIndexOf('/')) ===
				itemLocation.pathname ||
			currentLocation.pathname.substring(0, currentLocation.pathname.lastIndexOf('/board/')) ===
				itemLocation.pathname ||
			currentLocation.pathname.substring(0, currentLocation.pathname.lastIndexOf('/calendar/')) ===
				itemLocation.pathname ||
			currentLocation.pathname.substring(0, currentLocation.pathname.lastIndexOf('/edit/')) ===
				itemLocation.pathname;

		return pathNameMatchesNew && isSectionMatched;
	};

export const QueueItem = forwardRef<HTMLElement, QueueItemProps>(
	({ Badge = BadgeDI, MenuItem = MenuItemDI, queue, section, ...rest }, ref) => {
		const [{ route, location }] = useRouter();
		const [queueId] = usePathParam('queueId');
		const [practice] = usePathParam('practiceType');
		const [projectKey] = usePathParam('projectKey');
		const pathCategory = toItsmPractice(practice);
		const { formatMessage } = useIntl();
		const [pathCategorySelectedSection, { updatePathCategorySelectedQueueSection }] =
			useSelectedQueueSectionByCategory(pathCategory);
		const href = `/jira/servicedesk${queue.url}`;
		const isSelected = route != null && queueId === queue.id && location.pathname.startsWith(href);
		const locale = useLocale();
		const queueCategory = queue.category || NO_CATEGORY;
		const queueCustomContext = useQueueCustomRouterContext(projectKey);
		const queuesContextTemp = fg('add-custom-context-to-nav3-group-fetch-calls')
			? queueCustomContext
			: undefined;
		const [priorityGroups] = usePriorityGroups(queueCategory, queuesContextTemp);
		const [selectedGroupId] = useSelectedGroupIdByCategory(queueCategory);
		const [lastSelectedSection] = useSelectedQueueSectionByCategory(queueCategory);
		const actualSelectedSection = useMemo(() => {
			if (isSelected) {
				return calculateSelectedSection(
					queue,
					priorityGroups,
					lastSelectedSection,
					section,
					selectedGroupId,
				);
			}
			return lastSelectedSection;
		}, [isSelected, lastSelectedSection, priorityGroups, queue, section, selectedGroupId]);

		useEffect(() => {
			if (
				isSelected &&
				actualSelectedSection === section &&
				actualSelectedSection !== pathCategorySelectedSection
			) {
				updatePathCategorySelectedQueueSection(projectKey || '', queueCategory, section);
			}
		}, [
			actualSelectedSection,
			isSelected,
			pathCategory,
			pathCategorySelectedSection,
			projectKey,
			queue,
			queueCategory,
			section,
			updatePathCategorySelectedQueueSection,
		]);

		const getFormattedRelativeLastRefreshedTime = useCallback(() => {
			if (queue.badgeCount === undefined) {
				return formatMessage(tooltipMessages.loadingIssueCountTooltip);
			}
			const lastRefreshedTime =
				queue.lastRefreshedTime === 0 || queue.lastRefreshedTime === undefined
					? Date.now()
					: queue.lastRefreshedTime;
			return formatMessage(tooltipMessages.showLastRefreshedTimeTooltip, {
				lastUpdate: formatDistanceStrictWithLocale(lastRefreshedTime, Date.now(), locale, true),
			});
		}, [formatMessage, locale, queue.badgeCount, queue.lastRefreshedTime]);

		const shouldShowBadge = useCallback(() => {
			if (section !== QueueListSection.ALL_QUEUES) {
				return true;
			}
			return isSelected && pathCategorySelectedSection === section;
		}, [isSelected, pathCategorySelectedSection, section]);

		const issueCountBadge = shouldShowBadge() && (
			<Tooltip content={() => getFormattedRelativeLastRefreshedTime()}>
				{(tooltipProps) => (
					<div data-is-queue-issue-count-badge {...tooltipProps}>
						<Badge max={MAX_BADGE_COUNT}>
							{isNumber(queue.badgeCount) ? toNumber(queue.badgeCount) : '-'}
						</Badge>
					</div>
				)}
			</Tooltip>
		);
		const starButton = <StarButton isStarred={queue.starred} queueId={queue.id} />;
		const starrableBadge = (
			<>
				<div data-is-star-queue-button>{starButton}</div>
				{issueCountBadge}
			</>
		);
		const issueCountErrorBadge = (
			<>
				<div data-is-star-queue-button>{starButton}</div>
				<IssueCountErrorMessage queue={queue} />
			</>
		);
		const shouldShowIssueCountError = useCallback(() => {
			return queue.shouldSkipIssueCountRefresh && shouldShowBadge();
		}, [queue.shouldSkipIssueCountRefresh, shouldShowBadge]);

		const notificationBadge = shouldShowIssueCountError() ? issueCountErrorBadge : starrableBadge;

		return (
			<QueueItemContainer>
				<MenuItem
					ref={ref}
					analytics={{
						itemId: `queue-${queue.id}`,
						itemType: 'queue',
						sidebarSection: QueueListSection[section],
					}}
					href={href}
					selectedOn={matchUrlForSelectedQueue(actualSelectedSection, section)}
					onClick={() =>
						updatePathCategorySelectedQueueSection(
							projectKey || '',
							queue.category || NO_CATEGORY,
							section,
						)
					}
					iconAfter={notificationBadge}
					aria-label={queue.name}
					{...rest}
				>
					{queue.name}
				</MenuItem>
			</QueueItemContainer>
		);
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const QueueItemContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	"[data-is-star-queue-button='true']": {
		display: 'none',
		marginRight: token('space.050'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		"[role='presentation']": {
			/* Remove inapplicable a11y margin causing unexpected layout growth */
			margin: 'unset',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			button: {
				margin: 'unset',
			},
		},
	},
	'&:hover, &:focus-within': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		"[data-is-star-queue-button='true']": {
			display: 'block',
		},
	},
});
